import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { clearRemixIntent, getPersistedRemix } from '@/lib/remix-intent';

/**
 * Replays a persisted `?remix=<appId>` intent after auth (see `remix-intent.ts`).
 * Mounted at the dashboard layout level — the same place ReferralBonusModal sits —
 * so it fires once the user lands anywhere in the dashboard post-signup, even if
 * onboarding dropped the original query param. Routes to `/dashboard/apps?remix=<id>`,
 * where the Apps page opens the remix modal.
 */
export function RemixIntentHandler(): null {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const appId = getPersistedRemix();
    if (!appId) return;

    // Clear first so this can never loop, regardless of navigation outcome.
    clearRemixIntent();

    // If we already landed on the apps route with this remix param (returnUrl
    // preserved it), the Apps page handles it — nothing to do.
    const alreadyThere =
      location.pathname === '/dashboard/apps' &&
      new URLSearchParams(location.search).get('remix') === appId;
    if (alreadyThere) return;

    navigate(`/dashboard/apps?remix=${encodeURIComponent(appId)}`);
  }, [navigate, location.pathname, location.search]);

  return null;
}
